/* eslint-disable react/require-default-props */
import React from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import parse from "html-react-parser";
import { FlexColumn, FlexRow, Typography } from "design-system";

function SegmentedProgress({ totalSteps, filledSteps, percent, label }) {
  return (
    <FlexColumn
      align="start"
      fullWidth={false}
      className="cc-preview-progress"
      aria-label={label}
    >
      <FlexRow
        align="center"
        justify="between"
        fullWidth
        className="cc-preview-progress__row"
      >
        <Typography component="span" className="cc-preview-progress__label">
          {label}
        </Typography>
        <Typography component="span" className="cc-preview-progress__value">
          {Math.round(percent)}%
        </Typography>
      </FlexRow>
      <FlexRow
        align="start"
        fullWidth
        className="cc-preview-progress__segments"
        aria-hidden
      >
        {Array.from({ length: totalSteps }).map((_, index) => (
          <FlexRow
            key={`segment-${index + 1}`}
            inline
            fullWidth={false}
            className={`cc-preview-progress__segment${
              index < filledSteps ? " cc-preview-progress__segment--filled" : ""
            }`}
            aria-hidden
          />
        ))}
      </FlexRow>
    </FlexColumn>
  );
}

SegmentedProgress.propTypes = {
  totalSteps: PropTypes.number.isRequired,
  filledSteps: PropTypes.number.isRequired,
  percent: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
};

function PreviewLogo({ src }) {
  if (!src) return null;
  return (
    <Typography
      component="img"
      src={src}
      alt=""
      className="cc-preview-shell__logo"
    />
  );
}

PreviewLogo.propTypes = {
  src: PropTypes.string,
};

function getFirstRecipientName(recipient) {
  if (!recipient?.trim()) {
    return "";
  }
  return recipient.split(",")[0]?.trim() || recipient.trim();
}

export default function ClientConnectPreviewHeader({
  companyLogo,
  recipient,
  senderName,
  sentDate,
  currentStep,
  totalSteps,
  completedCount,
}) {
  const intl = useIntl();
  const filledSteps = Math.min(
    totalSteps,
    Math.max(currentStep, completedCount),
  );
  const percent = totalSteps > 0 ? (filledSteps / totalSteps) * 100 : 0;

  return (
    <FlexRow
      align="start"
      justify="between"
      fullWidth
      className="cc-preview-shell__header"
    >
      <FlexColumn
        align="start"
        fullWidth={false}
        className="cc-preview-shell__header-left"
      >
        <FlexRow
          align="center"
          fullWidth={false}
          className="cc-preview-shell__logo-row"
        >
          <PreviewLogo src={companyLogo} />
        </FlexRow>
        <Typography component="h1" className="cc-preview-shell__greeting">
          {intl.formatMessage(
            { id: "client-connect-preview-hey" },
            { name: getFirstRecipientName(recipient) },
          )}
        </Typography>
        <Typography component="p" className="cc-preview-shell__meta">
          {intl.formatMessage(
            { id: "client-connect-preview-sent-from" },
            { sender: senderName, date: sentDate },
          )}
        </Typography>
      </FlexColumn>
      <SegmentedProgress
        totalSteps={totalSteps}
        filledSteps={filledSteps}
        percent={percent}
        label={intl.formatMessage(
          { id: "client-connect-preview-step-of" },
          { current: currentStep, total: totalSteps },
        )}
      />
    </FlexRow>
  );
}

ClientConnectPreviewHeader.propTypes = {
  companyLogo: PropTypes.string,
  recipient: PropTypes.string,
  senderName: PropTypes.string,
  sentDate: PropTypes.string,
  currentStep: PropTypes.number.isRequired,
  totalSteps: PropTypes.number.isRequired,
  completedCount: PropTypes.number.isRequired,
};
